import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../../core/redux/api/reduxStandardApiAction';
import { IAppState } from '../../core/redux/IAppState';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { hasTrippedBreaker, updateBreaker } from '../../core/redux/api/circuitBreaker/module';
import { addError, addLoading, getError, getLoading, removeLoadingAndError } from '../../core/redux/api/module';
import { API_APP_CONFIG } from '../../core/redux/api/endpoints';
import { ResortToolsConfig } from './resortToolsConfig';
import { DEFAULT_CONFIG } from './defaultConfig';
import { getResource } from '../../language/resources/getResource';


export const KEY_ACTION = ACTION_TYPE.APP_CONFIG_REQUEST;
export const ACTIONS    = [
  ACTION_TYPE.APP_CONFIG_REQUEST,
  ACTION_TYPE.APP_CONFIG_RESPONSE,
  ACTION_TYPE.APP_CONFIG_ERROR,
];


/** Whether a config load is in flight. One at a time for the whole app. */
export const isConfigLoading = (state: IAppState): boolean => !!getLoading(state, KEY_ACTION, KEY_ACTION);


/** Whether the last config load failed. Kept until the next one starts. */
export const hasConfigLoadFailed = (state: IAppState): boolean => !!getError(state, KEY_ACTION, KEY_ACTION);


/**
 * An action that can be dispatched to retrieve app config from the server. This app config tells us which features are
 * on/off, how the circuit breaker should work etc.
 */
export const getConfigAction = (resortId: number) =>  makeApiAction(
    {
      method: 'GET',
      headers: (state: IAppState) => ({
        'content-type': 'application/json',
        'Authorization': `Bearer ${state.auth.bearerToken}`,
      }),
      endpoint: () => API_APP_CONFIG(resortId),
      types: ACTIONS as RsaaTypes,
      bailout: (state: IAppState) => !!getLoading(state, KEY_ACTION, KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
    });



const reducer: ApiReducer<ResortToolsConfig> = (state, action, error, isLoading, payload: ResortToolsConfig) => {
  if (isLoading) {
    return addLoading(state, KEY_ACTION, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(
      withBreaker,
      KEY_ACTION,
      KEY_ACTION,
      getResource(state.locale, 'GET_CONFIG_ACTION_ERROR'),
    );
  }
  const noLoadingOrError = removeLoadingAndError(withBreaker, KEY_ACTION, KEY_ACTION);

  //Resort specific configs might be missing something from the default, so we merge them
  const mergedState = {...DEFAULT_CONFIG, ...payload};

  return {
    ...noLoadingOrError,
    resortToolsConfig: {
      ...mergedState,
      isConfigLoaded: true,
    }
  };
}


/**
 * Maps getConfigActions onto old app state to make a new state.
 */
export const getConfigReducer = makeApiReducer<ResortToolsConfig>(ACTIONS, KEY_ACTION, reducer);
